import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Tabs,
    Tab,
    Typography,
    Paper,
    Grid,
    Alert,
    CircularProgress,
    Button,
    Stack,
    Menu,
    MenuItem,
} from "@mui/material";
import { Refresh as RefreshIcon, GetApp as ExportIcon, ArrowDropDown as ArrowDropDownIcon } from "@mui/icons-material";

// Import report components
import OrdersReport from "../../../components/admin/reports/OrdersReport";
import WarrantyReport from "../../../components/admin/reports/WarrantyReport";
import InsuranceReport from "../../../components/admin/reports/InsuranceReport";
import CustomersReport from "../../../components/admin/reports/CustomersReport";
import UsersReport from "../../../components/admin/reports/UsersReport";
import SalesFunnelReport from "../../../components/admin/reports/SalesFunnelReport";
import DashboardReport from "../../../components/admin/reports/DashboardReport";

// Import report APIs
import {
    useGenerateOrdersReportQuery,
    useGenerateWarrantyReportQuery,
    useGenerateInsuranceReportQuery,
    useGenerateCustomersReportQuery,
    useGenerateUsersReportQuery,
    useGenerateSalesFunnelReportQuery,
    useGenerateDashboardReportQuery,
} from "../../../store/reports/reportsApis";

// Import export utilities
import {
    exportOrdersReport,
    exportWarrantyReport,
    exportInsuranceReport,
    exportCustomersReport,
    exportUsersReport,
} from "../../../helpers/exportUtils";

const ReportsContainer = (props) => {
    const {
        activeReportType,
        filters,
        setActiveReportType,
        updateFilters,
        resetFilters,
        setReportData,
        setLoading,
        setError,
        setExporting,
        clearAllReports,
        clearReportData,
    } = props;

    const [tabValue, setTabValue] = useState(0);
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const reportDataRef = useRef({});

    // Report type mapping
    const reportTypes = [
        { id: "orders", label: "Orders Report", component: OrdersReport },
        { id: "warranty", label: "Warranty Report", component: WarrantyReport },
        { id: "insurance", label: "Insurance Report", component: InsuranceReport },
        { id: "customers", label: "Customers Report", component: CustomersReport },
        { id: "users", label: "Users Report", component: UsersReport },
        { id: "sales-funnel", label: "Sales Funnel", component: SalesFunnelReport },
        { id: "dashboard", label: "Dashboard Report", component: DashboardReport },
    ];

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        const selectedReportType = reportTypes[newValue].id;
        setActiveReportType(selectedReportType);
    };

    // Handle filter updates
    const handleFilterUpdate = (newFilters) => {
        updateFilters({ reportType: activeReportType, filters: newFilters });
    };

    // Handle filter reset
    const handleFilterReset = () => {
        resetFilters(activeReportType);
    };

    // Store report data from child components
    const handleReportDataUpdate = (reportType, data) => {
        reportDataRef.current[reportType] = data;
    };

    // Handle export menu
    const handleExportMenuOpen = (event) => {
        setExportMenuAnchor(event.currentTarget);
    };

    const handleExportMenuClose = () => {
        setExportMenuAnchor(null);
    };

    // Handle export
    const handleExport = (format = "csv") => {
        handleExportMenuClose();

        const currentReportData = reportDataRef.current[activeReportType];

        if (!currentReportData) {
            alert("No data to export. Please generate a report first.");
            return;
        }

        setExporting({ reportType: activeReportType, exporting: true });

        try {
            switch (activeReportType) {
                case "orders":
                    exportOrdersReport(currentReportData, format, "orders");
                    break;
                case "warranty":
                    exportWarrantyReport(currentReportData, format);
                    break;
                case "insurance":
                    exportInsuranceReport(currentReportData, format);
                    break;
                case "customers":
                    exportCustomersReport(currentReportData, format);
                    break;
                case "users":
                    exportUsersReport(currentReportData, format);
                    break;
                default:
                    alert("Export not available for this report type yet.");
            }
        } catch (error) {
            console.error("Export error:", error);
            alert("Failed to export report. Please try again.");
        } finally {
            setExporting({ reportType: activeReportType, exporting: false });
        }
    };

    // Handle refresh
    const handleRefresh = () => {
        clearReportData(activeReportType);
        // The query will automatically refetch due to RTK Query
    };

    // Get current report component
    const CurrentReportComponent = reportTypes[tabValue]?.component;

    return (
        <Box sx={{ width: "100%" }}>
            {/* Header */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <Typography variant="h5" component="h1">
                            Reports & Analytics
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Generate comprehensive reports for business insights
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} size="small">
                                Refresh
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<ExportIcon />}
                                endIcon={<ArrowDropDownIcon />}
                                onClick={handleExportMenuOpen}
                                size="small"
                            >
                                Export
                            </Button>
                            <Menu
                                anchorEl={exportMenuAnchor}
                                open={Boolean(exportMenuAnchor)}
                                onClose={handleExportMenuClose}
                            >
                                <MenuItem onClick={() => handleExport("csv")}>Export as CSV</MenuItem>
                                <MenuItem onClick={() => handleExport("excel")}>Export as Excel</MenuItem>
                            </Menu>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabs */}
            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                    {reportTypes.map((report, index) => (
                        <Tab
                            key={report.id}
                            label={report.label}
                            id={`report-tab-${index}`}
                            aria-controls={`report-tabpanel-${index}`}
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* Report Content */}
            <Paper sx={{ p: 2 }}>
                {CurrentReportComponent && (
                    <CurrentReportComponent
                        reportType={activeReportType}
                        filters={filters[activeReportType] || {}}
                        onFilterUpdate={handleFilterUpdate}
                        onFilterReset={handleFilterReset}
                        onExport={handleExport}
                        onRefresh={handleRefresh}
                        onReportDataUpdate={handleReportDataUpdate}
                    />
                )}
            </Paper>
        </Box>
    );
};

export default ReportsContainer;
